import React, { useState, useEffect } from 'react';
import {
  FloatingDrawer,
  FloatingDrawerHeader,
  FloatingDrawerBody,
  FloatingDrawerFooter,
} from '@/components/ui/floating-drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RoleBadge } from './RoleBadge';
import { TeamMember, UpdateMemberData } from '@/lib/api/services/teamService';
import { useAuth } from '@/components/AuthProvider';
import { useBusinessAccounts } from '@/hooks/useBusinessAccounts';
import { Building2, Phone, CheckCircle, AlertCircle, UserCog, Info } from 'lucide-react';
import {
  hasOrgWideBusinessAccountAccess,
  ORG_WIDE_ACCESS_NOTE,
} from '@/lib/teams/businessAccountAccess';

interface MinimalEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: TeamMember;
  onSave: (updatedUser: UpdateMemberData & { id: string }) => void;
}

export const MinimalEditDialog: React.FC<MinimalEditDialogProps> = ({
  open,
  onOpenChange,
  user,
  onSave
}) => {
  const parseAccessFlag = (value: unknown): boolean => {
    return value === true || value === 1 || value === '1' || value === 'true';
  };
  const getHasAllAccessFromPermissions = (permissions: unknown): boolean => {
    if (!permissions) return false;
    if (typeof permissions === 'string') {
      try {
        const parsed = JSON.parse(permissions);
        return parseAccessFlag(parsed?.has_all_conversations_access);
      } catch {
        return false;
      }
    }
    return parseAccessFlag((permissions as any)?.has_all_conversations_access);
  };

  const { user: currentUser } = useAuth();
  const roleRaw = (currentUser?.role || '').toString().toUpperCase();
  const isSuperAdmin = !!(currentUser as any)?.is_super_admin;
  const isAdmin = roleRaw === 'ADMIN' || isSuperAdmin;
  const [formData, setFormData] = useState(() => ({
    name: user.name,
    email: user.email,
    role: (user.role || 'USER').toString().toUpperCase(),
    status: (user as any).is_active ? 'active' : 'inactive',
  }));

  // Parse existing phone into country code + digits for RIDER
  const parseExistingPhone = (phone?: string) => {
    if (!phone) return { code: '+91', digits: '' };
    const known = ['+971', '+880', '+977', '+94', '+92', '+65', '+60', '+61', '+44', '+91', '+1'];
    for (const c of known) {
      if (phone.startsWith(c)) return { code: c, digits: phone.slice(c.length) };
    }
    return { code: '+91', digits: phone };
  };
  const parsed = parseExistingPhone(user.phone);
  const [riderCountryCode, setRiderCountryCode] = useState(parsed.code);
  const [riderPhone, setRiderPhone] = useState(parsed.digits);
  const [riderPhoneError, setRiderPhoneError] = useState('');

  const { data: businessAccounts, isLoading: isLoadingBusinessAccounts } = useBusinessAccounts();
  const [selectedBusinessAccounts, setSelectedBusinessAccounts] = useState<string[]>([]);
  const [accountPermissions, setAccountPermissions] = useState<Record<string, { has_all_conversations_access: boolean }>>({});

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        role: (user.role || 'USER').toString().toUpperCase(),
        status: (user as any).is_active ? 'active' : 'inactive',
      });
      const p = parseExistingPhone(user.phone);
      setRiderCountryCode(p.code);
      setRiderPhone(p.digits);
      setRiderPhoneError('');

      // Initialize assignments from user data
      if (user.assigned_accounts) {
        const ids = user.assigned_accounts.map(a => a.id);
        setSelectedBusinessAccounts(ids);

        const perms: Record<string, { has_all_conversations_access: boolean }> = {};
        user.assigned_accounts.forEach(a => {
          perms[a.id] = {
            has_all_conversations_access: getHasAllAccessFromPermissions(a.permissions)
          };
        });
        setAccountPermissions(perms);
      } else {
        // Fallback: auto-select if only one business account exists
        if (businessAccounts && businessAccounts.length === 1) {
          setSelectedBusinessAccounts([businessAccounts[0].id]);
        } else {
          setSelectedBusinessAccounts([]);
        }
        setAccountPermissions({});
      }
    }
  }, [user]);

  /**
   * Does this member get every account in the org regardless of the assignment
   * rows? Mirrors the backend gate — see lib/teams/businessAccountAccess.ts.
   */
  const hasOrgWideAccess = hasOrgWideBusinessAccountAccess({
    role: formData.role,
    isOrganizationOwner: user.is_organization_owner,
    isSuperAdmin: user.is_super_admin,
  });

  /**
   * Keep the selection equal to "all accounts" while access is org-wide, so the
   * list, the "N selected" summary and what a save persists can never disagree.
   *
   * This used to be an `if (role === 'ADMIN')` effect that ran over an EDITABLE
   * list, which is what caused the reported bug: it overwrote the member's
   * saved selection on every open (the save itself was fine — the DB really did
   * hold one row), and because its deps did not include the selection, a
   * background `businessAccounts` refetch could also silently re-select
   * everything mid-edit. The list is now read-only whenever this applies, so
   * there is no user choice left to clobber.
   */
  const wasOrgWideRef = React.useRef(hasOrgWideAccess);
  useEffect(() => {
    if (isLoadingBusinessAccounts || !businessAccounts) return;

    if (hasOrgWideAccess) {
      const allIds = businessAccounts.map(ba => ba.id);
      const areAllSelected =
        allIds.length === selectedBusinessAccounts.length &&
        allIds.every(id => selectedBusinessAccounts.includes(id));
      if (!areAllSelected) {
        setSelectedBusinessAccounts(allIds);
        setAccountPermissions(
          Object.fromEntries(allIds.map(id => [id, { has_all_conversations_access: true }])),
        );
      }
    } else if (wasOrgWideRef.current) {
      // Just dropped from an org-wide role (e.g. Admin -> Agent) — restore the
      // member's STORED selection rather than leaving every account ticked from
      // the locked view, which would silently grant everything on save.
      const stored = user.assigned_accounts?.map(a => a.id) ?? [];
      setSelectedBusinessAccounts(stored);
      setAccountPermissions(
        Object.fromEntries(
          (user.assigned_accounts ?? []).map(a => [
            a.id,
            { has_all_conversations_access: getHasAllAccessFromPermissions(a.permissions) },
          ]),
        ),
      );
    }

    wasOrgWideRef.current = hasOrgWideAccess;
  }, [hasOrgWideAccess, businessAccounts, isLoadingBusinessAccounts]);


  const isSingleAccount = !!(businessAccounts && businessAccounts.length === 1);

  const handleBusinessAccountToggle = (businessAccountId: string) => {
    // Belt and braces: the rows are already non-interactive while access is
    // org-wide, but a mutation here can never be honoured by the server, so
    // refuse it at the source too.
    if (hasOrgWideAccess) return;
    // Prevent deselecting when it's the only business account
    if (isSingleAccount) return;
    setSelectedBusinessAccounts(prev =>
      prev.includes(businessAccountId)
        ? prev.filter(id => id !== businessAccountId)
        : [...prev, businessAccountId]
    );
  };

  const handleValidationToggle = (businessAccountId: string, checked: boolean) => {
    setAccountPermissions(prev => ({
      ...prev,
      [businessAccountId]: { ...prev[businessAccountId], has_all_conversations_access: checked }
    }));
  };

  const handleSelectAll = () => {
    if (businessAccounts) {
      setSelectedBusinessAccounts(businessAccounts.map(ba => ba.id));
    }
  };

  const handleDeselectAll = () => {
    // Same reason as the row toggle: emptying the list for an org-wide member
    // would look like it worked and then come back on the next open.
    if (hasOrgWideAccess) return;
    setSelectedBusinessAccounts([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedRole = (formData.role || 'USER').toString().toUpperCase();

    // Validate RIDER mobile
    if (normalizedRole === 'RIDER') {
      const digits = riderPhone.replace(/\D/g, '');
      if (digits.length !== 10) {
        setRiderPhoneError('Please enter a valid 10-digit mobile number.');
        return;
      }
    }

    // Construct assignments payload
    const assignments = selectedBusinessAccounts.map(accountId => ({
      business_account_id: accountId,
      permissions: {
        // For ADMIN/USER, force full access. For AGENT, use selection (default false)
        has_all_conversations_access: normalizedRole === 'AGENT'
          ? (accountPermissions[accountId]?.has_all_conversations_access || false)
          : true
      }
    }));
    const hasAllConversationsAccess = normalizedRole !== 'AGENT'
      ? true
      : assignments.some(a => a.permissions.has_all_conversations_access === true);

    onSave({
      id: user.id,
      name: formData.name,
      email: formData.email,
      role: normalizedRole as 'ADMIN' | 'AGENT' | 'USER',
      status: formData.status,
      assignments,
      hasAllConversationsAccess,
      ...(normalizedRole === 'RIDER' && riderPhone.replace(/\D/g, '').length === 10
        ? { phone: `${riderCountryCode}${riderPhone.replace(/\D/g, '')}` }
        : {}),
    });
  };

  return (
    <FloatingDrawer
      open={open}
      onOpenChange={onOpenChange}
      side="right"
      width={560}
      className="overflow-hidden"
    >
      <FloatingDrawerHeader
        title="Edit team member"
        subtitle="Update member information"
        icon={<UserCog className="h-4 w-4" />}
      />

      {/* The submit button lives in the footer, OUTSIDE this form — they are
          associated by `form="edit-member-form"`, which works across the
          drawer's flex layout exactly as it did across the dialog's. */}
      <FloatingDrawerBody>
        <form id="edit-member-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Role</Label>
            {/* `modal={false}` is REQUIRED for a Select inside a
                FloatingDrawer/Sheet — with the default `modal`, Radix's nested
                dismissable-layer stack never treats a click in the drawer body
                as "outside", so the list gets stuck open. See the wrapper in
                ui/select.tsx and floatingDrawerNestedDismiss.test.ts. */}
            <Select
              value={formData.role}
              onValueChange={(value) => setFormData(prev => ({ ...prev, role: value as 'ADMIN' | 'AGENT' | 'USER' }))}
              modal={false}
            >
              <SelectTrigger data-testid="edit-member-role-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USER">
                  <RoleBadge role="USER" size="sm" showIcon={false} />
                </SelectItem>
                <SelectItem value="AGENT">
                  <RoleBadge role="AGENT" size="sm" showIcon={false} />
                </SelectItem>
                <SelectItem value="RIDER">
                  <RoleBadge role="RIDER" size="sm" showIcon={false} />
                </SelectItem>
                {isAdmin && (
                  <SelectItem value="ADMIN">
                    <RoleBadge role="ADMIN" size="sm" showIcon={false} />
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {formData.role === 'RIDER' && (
            <div className="space-y-2">
              <Label htmlFor="edit-rider-mobile">
                Mobile Number <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-2">
                <Select value={riderCountryCode} onValueChange={setRiderCountryCode} modal={false}>
                  <SelectTrigger className="w-[110px] shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="+91">🇮🇳 +91</SelectItem>
                    <SelectItem value="+1">🇺🇸 +1</SelectItem>
                    <SelectItem value="+44">🇬🇧 +44</SelectItem>
                    <SelectItem value="+971">🇦🇪 +971</SelectItem>
                    <SelectItem value="+61">🇦🇺 +61</SelectItem>
                    <SelectItem value="+65">🇸🇬 +65</SelectItem>
                    <SelectItem value="+60">🇲🇾 +60</SelectItem>
                    <SelectItem value="+880">🇧🇩 +880</SelectItem>
                    <SelectItem value="+92">🇵🇰 +92</SelectItem>
                    <SelectItem value="+94">🇱🇰 +94</SelectItem>
                    <SelectItem value="+977">🇳🇵 +977</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  id="edit-rider-mobile"
                  type="tel"
                  inputMode="numeric"
                  placeholder="Enter 10-digit number"
                  value={riderPhone}
                  maxLength={10}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setRiderPhone(val);
                    setRiderPhoneError(val.length > 0 && val.length !== 10 ? 'Enter a valid 10-digit number.' : '');
                  }}
                  className={riderPhoneError ? 'border-red-500' : ''}
                />
              </div>
              {riderPhoneError && (
                <p className="text-xs text-red-500">{riderPhoneError}</p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label>Status</Label>
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <span className="text-sm">Active</span>
              <Switch
                checked={formData.status === 'active'}
                onCheckedChange={(checked) =>
                  setFormData(prev => ({
                    ...prev,
                    status: checked ? 'active' : 'inactive'
                  }))
                }
              />
            </div>
          </div>

          <div className="space-y-3 pt-2 border-t">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Business Accounts Access</Label>
              {/* Hidden while access is org-wide: these buttons could only ever
                  re-assert what is already forced, and "Deselect All" would look
                  like it worked and then revert. */}
              {!isSingleAccount && !hasOrgWideAccess && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                    disabled={isLoadingBusinessAccounts}
                  >
                    Select All
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDeselectAll}
                    disabled={isLoadingBusinessAccounts}
                  >
                    Deselect All
                  </Button>
                </div>
              )}
            </div>

            {hasOrgWideAccess ? (
              <div
                className="flex items-start gap-2 rounded-lg border border-[#B2DDFF] bg-[#EFF8FF] px-3 py-2.5 text-xs text-[#175CD3]"
                data-testid="edit-member-org-wide-note"
              >
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <p>{ORG_WIDE_ACCESS_NOTE}</p>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                Choose which business accounts this team member can access and configure permissions.
              </div>
            )}

            {isLoadingBusinessAccounts ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="ml-2 text-sm text-muted-foreground">Loading business accounts...</span>
              </div>
            ) : businessAccounts && businessAccounts.length > 0 ? (
              <Card>
                <CardContent className="p-4 space-y-3">
                  {businessAccounts.map((businessAccount) => {
                    const isSelected = selectedBusinessAccounts.includes(businessAccount.id);
                    const hasAllAccess = accountPermissions[businessAccount.id]?.has_all_conversations_access || false;

                    return (
                      <div
                        key={businessAccount.id}
                        className={`flex flex-col p-3 rounded-lg border transition-colors ${isSelected
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50'
                          }`}
                      >
                        {/* Account Selection Row. Not interactive while access is
                            org-wide — accepting a click the server will ignore is
                            exactly what made the selection appear to revert. */}
                        <div
                          className={`flex items-center justify-between w-full ${isSingleAccount || hasOrgWideAccess ? 'cursor-default' : 'cursor-pointer'}`}
                          onClick={
                            hasOrgWideAccess
                              ? undefined
                              : () => handleBusinessAccountToggle(businessAccount.id)
                          }
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <div className="font-medium">{businessAccount.name}</div>
                                <div className="text-sm text-muted-foreground flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {businessAccount.phone_number}
                                </div>
                              </div>
                            </div>
                            <Badge
                              variant={businessAccount.is_active ? "default" : "secondary"}
                              className="text-xs"
                            >
                              {businessAccount.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </div>

                          <div className="flex items-center">
                            {isSelected ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
                            )}
                          </div>
                        </div>

                        {/* Permissions Row (Visible only if selected and role is AGENT) */}
                        {isSelected && formData.role === 'AGENT' && (
                          <div className="mt-3 pl-9 border-t pt-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`edit-perm-all-${businessAccount.id}`}
                                checked={hasAllAccess}
                                onCheckedChange={(checked) => handleValidationToggle(businessAccount.id, !!checked)}
                              />
                              <Label
                                htmlFor={`edit-perm-all-${businessAccount.id}`}
                                className="text-sm cursor-pointer"
                              >
                                View All Conversations
                              </Label>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 ml-6">
                              Can see and manage all conversations for this business account.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-6 text-center">
                  <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No business accounts found. Create a business account first.
                  </p>
                </CardContent>
              </Card>
            )}

            {selectedBusinessAccounts.length > 0 && (
              <div className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                {selectedBusinessAccounts.length} business account{selectedBusinessAccounts.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>
        </form>
      </FloatingDrawerBody>

      <FloatingDrawerFooter>
        <Button
          type="button"
          variant="outline"
          className="rounded-[8px]"
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button type="submit" form="edit-member-form">
          Save Changes
        </Button>
      </FloatingDrawerFooter>
    </FloatingDrawer>
  );
};
